import api from './api';
import type {
  Template,
  FieldMapping,
  AnalyzeFileResponse,
  TemplateCategory,
  IndustryCategory,
  CreateTemplateRequest,
  UpdateTemplateRequest,
  CreateMappingRequest,
} from '../types/template';

// 模板 API
export const templateApi = {
  // ========== 模板管理 ==========

  // 获取模板列表
  listTemplates: (params?: { category?: string; industry?: string; keyword?: string }) =>
    api.get<{ templates: Template[]; total: number }>('/templates', { params }),

  // 获取模板详情
  getTemplate: (id: string) => api.get<Template>(`/templates/${id}`),

  // 创建模板
  createTemplate: (data: CreateTemplateRequest) =>
    api.post<Template>('/templates', data),

  // 更新模板
  updateTemplate: (id: string, data: UpdateTemplateRequest) =>
    api.put<Template>(`/templates/${id}`, data),

  // 删除模板
  deleteTemplate: (id: string) =>
    api.delete<void>(`/templates/${id}`),

  // 获取模板分类
  getCategories: () =>
    api.get<{ categories: TemplateCategory[]; industries: IndustryCategory[] }>('/templates/categories'),

  // ========== 文件分析与字段映射 ==========

  // 上传并分析文件
  analyzeFile: async (templateId: string, file: File): Promise<AnalyzeFileResponse> => {
    const formData = new FormData();
    formData.append('file', file);

    const token = localStorage.getItem('token') || '';
    const baseURL =
      (import.meta as { env?: { VITE_API_BASE_URL?: string } }).env?.VITE_API_BASE_URL ||
      '/api/v1';

    const response = await fetch(`${baseURL}/templates/${templateId}/analyze`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });

    if (!response.ok) {
      const text = await response.text();
      // 尝试解析 JSON 错误响应，提取 message 字段
      try {
        const errorJson = JSON.parse(text) as { code?: number; message?: string };
        if (errorJson.message) {
          throw new Error(errorJson.message);
        }
      } catch {
        // 不是 JSON 格式，使用原始文本
      }
      throw new Error(text || `HTTP ${response.status}`);
    }

    const json = await response.json() as {
      code: number;
      message: string;
      data: AnalyzeFileResponse;
    };

    if (json.code !== 0) {
      throw new Error(json.message || '文件分析失败');
    }

    return json.data;
  },

  // 验证字段映射
  validateMapping: (templateId: string, mappings: Record<string, string>) =>
    api.post<{
      valid: boolean;
      missing: string[];
      errors: string[];
    }>(`/templates/${templateId}/validate-mapping`, { mappings }),

  // 预览模板执行结果
  previewTemplate: async (
    templateId: string,
    file: File,
    mappings: Record<string, string>
  ): Promise<{
    preview: string[][];
    rowCount: number;
    templateId: string;
    mappings: Record<string, string>;
  }> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('mappings', JSON.stringify(mappings));

    const token = localStorage.getItem('token') || '';
    const baseURL =
      (import.meta as { env?: { VITE_API_BASE_URL?: string } }).env?.VITE_API_BASE_URL ||
      '/api/v1';

    const response = await fetch(`${baseURL}/templates/${templateId}/preview`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });

    if (!response.ok) {
      const text = await response.text();
      // 尝试解析 JSON 错误响应，提取 message 字段
      try {
        const errorJson = JSON.parse(text) as { code?: number; message?: string };
        if (errorJson.message) {
          throw new Error(errorJson.message);
        }
      } catch {
        // 不是 JSON 格式，使用原始文本
      }
      throw new Error(text || `HTTP ${response.status}`);
    }

    const json = await response.json() as {
      code: number;
      message: string;
      data: {
        preview: string[][];
        rowCount: number;
        templateId: string;
        mappings: Record<string, string>;
      };
    };

    if (json.code !== 0) {
      throw new Error(json.message || '预览失败');
    }

    return json.data;
  },

  // ========== 字段映射管理 ==========

  // 获取字段映射列表
  listMappings: (templateId?: string) =>
    api.get<{ mappings: FieldMapping[]; total: number }>('/templates/mappings', {
      params: templateId ? { templateId } : undefined,
    }),

  // 获取字段映射详情
  getMapping: (id: string) => api.get<FieldMapping>(`/templates/mappings/${id}`),

  // 保存字段映射
  saveMapping: (data: CreateMappingRequest) =>
    api.post<FieldMapping>('/templates/mappings', data),

  // 删除字段映射
  deleteMapping: (id: string) =>
    api.delete<void>(`/templates/mappings/${id}`),

  // ========== 模板应用 ==========

  // 应用模板执行工作流（直接执行，返回执行结果）
  // POST /templates/:id/apply
  // 支持多文件上传，每个文件对应一个输入节点
  applyTemplate: async (
    templateId: string,
    files: Array<{ nodeId: string; file: File }>,
    mappings?: Record<string, string>,
    virtualNodeTargets?: Record<string, string>
  ): Promise<{
    columns: string[];
    rows: Record<string, unknown>[];
    rowCount: number;
    templateId: string;
    templateName: string;
    workflowDefinition: Template['workflowDefinition'];
    mappings: Record<string, string>;
    metadata?: Record<string, unknown>;
    excelStyles?: Record<string, unknown>;
    is_grouped?: boolean;
    grouped_results?: Array<{
      group_key: string;
      columns: string[];
      rows: Record<string, unknown>[];
      rowCount: number;
    }>;
    nodeSchemas?: Record<string, string[]>;
    executionId?: string;
  }> => {
    const formData = new FormData();
    
    // 添加所有文件，按节点ID命名
    // 同时收集文件类型信息
    const fileTypes: Record<string, string> = {};
    files.forEach(({ nodeId, file }, index) => {
      formData.append(`file_${nodeId}`, file);
      // 根据文件扩展名确定类型
      const ext = file.name.toLowerCase().split('.').pop() || '';
      const fileType = ext === 'csv' ? 'csv' : 'excel';
      fileTypes[nodeId] = fileType;
      // 第一个文件也作为默认 file 字段
      if (index === 0) {
        formData.append('file', file);
        fileTypes['primary'] = fileType;
      }
    });
    
    // 添加文件类型信息
    formData.append('file_types', JSON.stringify(fileTypes));
    
    if (mappings) {
      formData.append('mappings', JSON.stringify(mappings));
    }

    if (virtualNodeTargets && Object.keys(virtualNodeTargets).length > 0) {
      formData.append('virtual_node_targets', JSON.stringify(virtualNodeTargets));
    }

    const token = localStorage.getItem('token') || '';
    const baseURL =
      (import.meta as { env?: { VITE_API_BASE_URL?: string } }).env?.VITE_API_BASE_URL ||
      '/api/v1';

    const response = await fetch(`${baseURL}/templates/${templateId}/apply`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });

    if (!response.ok) {
      const text = await response.text();
      try {
        const errorJson = JSON.parse(text) as { code?: number; message?: string };
        if (errorJson.message) {
          throw new Error(errorJson.message);
        }
      } catch {
        // 不是 JSON 格式，使用原始文本
      }
      throw new Error(text || `HTTP ${response.status}`);
    }

    const json = await response.json() as {
      code: number;
      message: string;
      data: {
        columns: string[];
        rows: Record<string, unknown>[];
        rowCount: number;
        templateId: string;
        templateName: string;
        workflowDefinition: Template['workflowDefinition'];
        mappings: Record<string, string>;
        metadata?: Record<string, unknown>;
        excelStyles?: Record<string, unknown>;
        is_grouped?: boolean;
        grouped_results?: Array<{
          group_key: string;
          columns: string[];
          rows: Record<string, unknown>[];
          rowCount: number;
        }>;
        nodeSchemas?: Record<string, string[]>;
        executionId?: string;
      };
    };

    if (json.code !== 0) {
      throw new Error(json.message || '模板应用失败');
    }

    return json.data;
  },
};

export default templateApi;
